<template>
    <v-container class="pa-12 fill-height align-start" fluid>
        <v-row no-gutters>
            <v-col>
                <v-btn small depressed outlined color="accent" :to="`/${$i18n.locale}/administrator/creative/projects`"
                ><v-icon left>mdi-arrow-left</v-icon> Back to projects</v-btn>
            </v-col>
        </v-row>
        <v-card outlined width="780" max-width="100%" class="mt-4">
            <v-card-title>New project</v-card-title>
            <div class="pa-4 pt-0">
                <form @submit.prevent="createProject">  
                    <v-row>
                        <v-col cols="6" class="py-0">
                            <v-text-field
                                dense
                                outlined
                                label="Title"
                                v-model="title"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6" class="py-0">
                            <v-select
                                dense
                                outlined
                                label="Topic"
                                :items="topics"
                                item-text="name"
                                item-value="id"
                                v-model="topicId"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-textarea
                        dense
                        outlined
                        no-resize
                        label="Short description"
                        v-model="shortDescription"
                    ></v-textarea>
                    <div class="subtitle-1">Description</div> 
                    <vue-editor class="mb-6" v-model="description" :editor-toolbar="customToolbar" />               
                    <v-row>
                        <v-col cols="4" class="py-0">
                            <v-text-field
                                dense
                                outlined
                                label="1st price"
                                type="number"
                                prepend-inner-icon="mdi-currency-usd"
                                v-model="firstPlacePrice"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" class="py-0">
                            <v-text-field
                                dense
                                outlined
                                label="2nd price"
                                type="number"
                                prepend-inner-icon="mdi-currency-usd"
                                v-model="secondPlacePrice"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="4" class="py-0">
                            <v-text-field
                                dense
                                outlined
                                label="3rd price"
                                type="number"
                                prepend-inner-icon="mdi-currency-usd"
                                v-model="thirdPlacePrice"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-menu
                        ref="bidDeadlineMenu"
                        v-model="bidDeadlineMenu"
                        close-on-content-click
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                        <v-text-field
                            dense
                            outlined
                            readonly
                            v-on="on"
                            label="Biding deadline"
                            prepend-inner-icon="mdi-calendar"
                            v-model="bidDeadline"
                        ></v-text-field>
                        </template>
                        <v-date-picker no-title v-model="bidDeadline"></v-date-picker>
                    </v-menu>
                    <v-card flat color="grey lighten-4">
                        <v-card-subtitle class="pb-0">Attach documents</v-card-subtitle>
                        <v-row class="d-flex justify-space-between align-center pa-4">
                            <v-col class="flex-grow-1 flex-shrink-0">
                                <v-text-field
                                    outlined
                                    dense
                                    hide-details
                                    label="File name"
                                    v-model="fileDisplayName"
                                ></v-text-field>
                            </v-col>
                            <v-col class="flex-grow-1 flex-shrink-1">
                                <v-file-input 
                                    dense
                                    outlined
                                    hide-details 
                                    prepend-icon="" 
                                    ref="fileUpload"
                                    label="File upload"
                                    class="mx-4"
                                    @change="documentAttachEventHandler"
                                ></v-file-input>
                            </v-col>
                            <v-col class="flex-grow-0 flex-shrink-0">
                                <v-btn 
                                    small 
                                    depressed
                                    color="primary" 
                                    @click="attachDocument"
                                    :disabled="!fileDisplayName || !fileObject.fileName"
                                    >Add</v-btn>
                            </v-col>
                        </v-row>
                        <template v-if="files.length > 0">
                            <v-card-text class="pt-0">
                                <v-chip
                                    close
                                    class="mr-2"
                                    color="primary"
                                    text-color="white"
                                    v-for="(file, index) in files"
                                    :key="index"
                                    @click:close="dettachDocument(index)"
                                >
                                    <v-icon left>mdi-file-document-outline</v-icon>
                                    {{ file.fileName }}
                                </v-chip>
                            </v-card-text>
                        </template>
                    </v-card>
                    <div class="text-center">
                        <v-btn 
                            small 
                            depressed 
                            color="primary"
                            class="mt-6" 
                            type="submit" 
                            :disabled="!isNewProjectFormValid || newProjectSubmiting"
                            :loading="newProjectSubmiting"
                        >Save</v-btn>
                    </div>
                </form>
            </div>
        </v-card>
    </v-container>
</template>

<script>
    import moment from 'moment'
    import { VueEditor } from "vue2-editor"

    export default {
        name: 'newProject',
        data: () => ({
            bidDeadlineMenu: false,
            bidDeadline: null,
            languageId: null,
            title: '',
            shortDescription: '',
            description: '',
            topicId: null,
            firstPlacePrice: null,
            secondPlacePrice: null,
            thirdPlacePrice: null,
            topics: [],
            fileDisplayName: '',
            fileObject: {
                base64String: '',
                fileName: '',
                fileExtension: ''
            },
            files: [],
            newProjectSubmiting: false,
            snackbar: {
                show: true,
                message: ''
            },
            customToolbar: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike', 'link'], 
                [{ 'list': 'ordered'}, { 'list': 'bullet' }]
            ],
        }),
        components: {
            VueEditor
        },
        computed: {
            isNewProjectFormValid() {
                if(
                    this.bidDeadline &&
                    this.title &&
                    this.description &&
                    this.topicId &&
                    this.firstPlacePrice &&
                    this.secondPlacePrice &&
                    this.thirdPlacePrice
                ) {
                    return true
                }
                return false
            }
        },
        mounted() {
            this.fetchTopics()
        },
        methods: {
            async fetchTopics() {
                await this.axios.get('/Topic/GetAllTopics', {
                    params: {
                        languageId: 1
                    }
                })
                .then(response => {
                    this.topics = response.data
                })
            },
            createProject() {
                this.newProjectSubmiting = true
                const data = {
                    bidDeadline: moment(this.bidDeadline, 'YYYY-MM-DD').toISOString(),
                    languageId: 1,
                    title: this.title,
                    shortDescription: this.shortDescription,
                    description: this.description,
                    topicId: this.topicId,
                    firstPlacePrice: parseInt(this.firstPlacePrice).toFixed(2),
                    secondPlacePrice: parseInt(this.secondPlacePrice).toFixed(2),
                    thirdPlacePrice: parseInt(this.thirdPlacePrice).toFixed(2),
                    files: this.files
                }
                this.axios.post('/Project/CreateProject', data)
                .then(response => {
                    this.clearForm()           
                    this.snackbar.message = 'Project created successfully'   
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.newProjectSubmiting = false
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
            },
            clearForm() {
                this.bidDeadline = null
                this.votingDeadline = null
                this.title = ''
                this.description = ''
                this.shortDescription = ''
                this.topicId = null
                this.firstPlacePrice = null
                this.secondPlacePrice = null
                this.thirdPlacePrice = null
                this.files = []
            },
            documentAttachEventHandler(event) {
                if(typeof event !== 'undefined') {
                    const file = event
                    this.fileObject.fileName = file.name.split('.')[0],
                    this.fileObject.fileExtension = file.name.split('.')[1]
                    var reader = new FileReader()
                    reader.readAsDataURL(file)
                    reader.onload = () => {
                        this.fileObject.base64String = reader.result.replace(/^data:.+;base64,/, '')
                    }
                }
            },
            attachDocument() {
                const fileObject = {
                    fileName: this.fileDisplayName,
                    file: this.fileObject
                }
                this.files.push(fileObject)
                this.fileDisplayName = ''
            },
            dettachDocument(index) {
                this.files.splice(index, 1)
            }
        }
    }
</script>

<style lang="scss" scoped></style>